import React, { Suspense } from 'react'

import { Switch, RouteComponentProps, Route, Redirect } from 'react-router-dom'

import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'
import { Loading } from '@elgatoylacaja/components'

const LazyDemoRoutes = React.lazy(() =>
  import(/* webpackChunkName: "DemoRoutes" */ './DemoRoutes')
)

const Terms = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './views/Terms')
)
const Intro = React.lazy(() =>
  import(/* webpackChunkName: "Intro" */ './views/Intro')
)
const OpinionVacuna = React.lazy(() =>
  import(/* webpackChunkName: "OpinionVacuna" */ './views/OpinionVacuna')
)
const VacunaAplicada = React.lazy(() =>
  import(/* webpackChunkName: "VacunaAplicada" */ './views/VacunaAplicada')
)

const Narrativa = React.lazy(() =>
  import(/* webpackChunkName: "Narrativa" */ './views/Narrativa')
)
const EvaluarNarrativas = React.lazy(() =>
  import(
    /* webpackChunkName: "EvaluarNarrativas" */ './views/EvaluarNarrativas'
  )
)

const RegresoresSalud = React.lazy(() =>
  import(/* webpackChunkName: "RegresoresSalud" */ './views/RegresoresSalud')
)
const RegresoresMedios = React.lazy(() =>
  import(/* webpackChunkName: "RegresoresMedios" */ './views/RegresoresMedios')
)
const RegresoresGeneral = React.lazy(() =>
  import(
    /* webpackChunkName: "RegresoresGeneral" */ './views/RegresoresGeneral'
  )
)
const Thanks = React.lazy(() =>
  import(/* webpackChunkName: "Thanks" */ './views/Thanks')
)

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(state => state.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(state => !state.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  const ExperimentRoutes = React.useMemo(
    () =>
      new LinkedRoutes([
        ['/terms', Terms],
        ['/intro', Intro],
        ...(appState.experiment.seVacunoOEnEnsayo
          ? ([
              ['/vacuna', VacunaAplicada],
              ['/narrativas', EvaluarNarrativas],
            ] as LinkedRoutes['paths'])
          : ([
              ['/vacuna/step1', OpinionVacuna],
              ['/vacuna/step2', Narrativa],
              ['/vacuna/step3', OpinionVacuna],
            ] as LinkedRoutes['paths'])),
        ['/regresores/salud', RegresoresSalud],
        ['/regresores/medios', RegresoresMedios],
        ['/regresores/general', RegresoresGeneral],
        ['/thanks', Thanks],
      ]).config(),
    [appState.experiment.seVacunoOEnEnsayo]
  )

  return (
    <Switch>
      <Redirect from="/" exact to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}
        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
